// Error reporting: sends errors to Honeybadger based on Flip feature flags.
// This is the frontend counterpart of AppMonitoring::Dispatcher on the backend.

import { isObject } from "common/utils/core";

const APP_MONITORING_KEY = "__APP_MONITORING__";

type NotifyOptions = {
  context?: Record<string, unknown>;
  params?: Record<string, unknown>;
  tags?: string;
  // HB-1942: top-level HB grouping field. Passed verbatim to Honeybadger.notify
  // as a sibling of `context`. Putting `fingerprint` inside `context` does NOT
  // change HB grouping — it must be a top-level option.
  fingerprint?: string;
  [key: string]: unknown;
};

// HB-1942: keys that Honeybadger.notify treats as top-level Notice fields
// (not as user-provided context). Add a key here only after confirming the
// HB client dist serializes it at the top level — see
// `node_modules/@honeybadger-io/js/dist/browser/honeybadger.js` payload-build
// path. Currently only `fingerprint` is supported; `tags` is intentionally
// NOT in this set because existing call sites rely on `tags` folding into
// context (notify.test.ts regression guard).
const HONEYBADGER_TOP_LEVEL_KEYS = new Set(["fingerprint"]);

const getFlags = (): { honeybadgerEnabled: boolean } => {
  const flags = (window as unknown as Record<string, unknown>)[APP_MONITORING_KEY] as
    | { flags?: { honeybadgerEnabled?: boolean } }
    | undefined;
  return {
    honeybadgerEnabled: flags?.flags?.honeybadgerEnabled === true
  };
};

type ResolvedOptions = {
  context: Record<string, unknown>;
  hbOptions: Record<string, unknown>;
};

const splitHbOptions = (
  source: Record<string, unknown>
): { keptForContext: Record<string, unknown>; hbOptions: Record<string, unknown> } => {
  const keptForContext: Record<string, unknown> = {};
  const hbOptions: Record<string, unknown> = {};
  for (const key of Object.keys(source)) {
    if (HONEYBADGER_TOP_LEVEL_KEYS.has(key)) {
      // Skip nullish values so an explicit `fingerprint: undefined` / `null`
      // does not reach hb.notify as a top-level key. Honors the documented
      // contract: a nullish value has nothing to serialize and would only
      // add noise on the outbound payload.
      if (source[key] != null) hbOptions[key] = source[key];
    } else {
      keptForContext[key] = source[key];
    }
  }
  return { keptForContext, hbOptions };
};

const resolveContext = (options: NotifyOptions | undefined): ResolvedOptions => {
  if (!options || !isObject(options)) return { context: {}, hbOptions: {} };
  if (isObject(options.context)) {
    const { context, params, ...rest } = options;
    const { keptForContext: restForContext, hbOptions } = splitHbOptions(
      rest as Record<string, unknown>
    );
    return {
      context: {
        ...(context as Record<string, unknown>),
        ...(isObject(params) ? (params as Record<string, unknown>) : {}),
        ...restForContext
      },
      hbOptions
    };
  }
  // Caller used the flat shape (no `context` key) — peel any HB top-level
  // keys off, the rest is the call-site context.
  const { keptForContext, hbOptions } = splitHbOptions(options as Record<string, unknown>);
  return { context: keptForContext, hbOptions };
};

const mergeGlobalContext = (callSiteContext: Record<string, unknown>): Record<string, unknown> => {
  const appMonitoring = (window as unknown as Record<string, unknown>)[APP_MONITORING_KEY] as
    | { context?: Record<string, unknown>; user?: Record<string, unknown> }
    | undefined;
  const globalContext = appMonitoring?.context || {};
  const globalUser = appMonitoring?.user || {};
  return { ...globalContext, ...globalUser, ...callSiteContext };
};

const canSendError = (): boolean => {
  const mon = (window as unknown as Record<string, unknown>)[APP_MONITORING_KEY] as
    | { notifyErrorCount?: number; flags?: { maxErrorsPerPage?: number } }
    | undefined;
  if (!mon) return true;
  const raw = Number(mon.flags?.maxErrorsPerPage);
  const max = Number.isFinite(raw) && raw > 0 ? raw : 20;
  const monRecord = mon as Record<string, unknown>;
  const count = (monRecord["notifyErrorCount"] as number) || 0;
  if (count >= max) return false;
  monRecord["notifyErrorCount"] = count + 1;
  return true;
};

const reportToHoneybadger = async (
  messageOrError: string | Error,
  options?: NotifyOptions
): Promise<void> => {
  try {
    const { getHoneybadger } = await import("./honeybadger-loader");
    const hb = await getHoneybadger();
    if (!hb || typeof hb.notify !== "function") return;

    const { context: callSiteContext, hbOptions } = resolveContext(options);
    const mergedContext = mergeGlobalContext(callSiteContext);

    hb.notify(messageOrError, { context: mergedContext, ...hbOptions });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }
};

export function notifyError(messageOrError: string | Error, options?: NotifyOptions): void {
  if (!canSendError()) return;

  const { honeybadgerEnabled } = getFlags();

  if (honeybadgerEnabled) {
    reportToHoneybadger(messageOrError, options).catch(() => {});
  } else {
    // Fail-open: log to console when Honeybadger is disabled or flag is unavailable,
    // so errors are never silently dropped.
    const error =
      messageOrError instanceof Error
        ? messageOrError
        : Object.assign(new Error(messageOrError), { name: "CustomError" });
    const { context: callSiteContext, hbOptions } = resolveContext(options);
    const mergedContext = mergeGlobalContext(callSiteContext);
    const hasContext = Object.keys(mergedContext).length > 0;
    const hasHbOptions = Object.keys(hbOptions).length > 0;
    if (hasContext && hasHbOptions) {
      console.error(error, mergedContext, hbOptions);
    } else if (hasContext) {
      console.error(error, mergedContext);
    } else if (hasHbOptions) {
      console.error(error, {}, hbOptions);
    } else {
      console.error(error);
    }
  }
}

// Expose notifyError for legacy Sprockets-bundled scripts that cannot use ES module imports.
// These scripts access it via window.__APP_MONITORING__.notifyError?.().
if (!(window as unknown as Record<string, unknown>)[APP_MONITORING_KEY]) {
  (window as unknown as Record<string, unknown>)[APP_MONITORING_KEY] = {};
}
((window as unknown as Record<string, unknown>)[APP_MONITORING_KEY] as Record<string, unknown>)[
  "notifyError"
] = notifyError;

// Patch queueError to delegate directly now that notifyError is available.
try {
  const monObj = (window as unknown as Record<string, unknown>)[APP_MONITORING_KEY] as Record<
    string,
    unknown
  >;
  monObj["queueError"] = notifyError;
} catch (e) {
  console.error(e);
}

// Flush errors queued before the monitoring bundle loaded.
export function flushPendingErrors(): void {
  try {
    const mon = (window as unknown as Record<string, unknown>)[APP_MONITORING_KEY] as
      | { pendingErrors?: [string | Error, NotifyOptions?][] }
      | undefined;
    const pending = mon?.pendingErrors;
    if (Array.isArray(pending) && pending.length > 0) {
      const batch = pending.splice(0, pending.length);
      batch.forEach(([msg, opts]) => notifyError(msg, opts));
    }
  } catch (e) {
    console.error(e);
  }
}

// Drain any errors queued by inline scripts before this bundle loaded.
flushPendingErrors();
